import { KitDeletionArea } from "@/components/KitDeletionArea";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import DisplayEditor from "@/components/display/display-editor/DisplayEditor";
import { GrantDialog } from "@/components/grant/GrantDialog";
import { Column } from "@/components/ui/flex";
import { useGroupKitExpertsData } from "@/data/use-group-kit-experts-data";
import { useKitData } from "@/data/use-kits-data";
import { useSkillData } from "@/data/use-skill-data";
import { useSkillExpertsData } from "@/data/use-skill-experts-data";
import { useSkillsData } from "@/data/use-skills-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { useGroupExpertsData } from "../../data/use-group-experts-data";
import { getInitials } from "../../lib/string";

export default function SkillSettingsPage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit, updateKit } = useKitData(groupUuid, kitUuid);
  const { skills } = useSkillsData(groupUuid, kitUuid);
  const skillUuid = skills[0]?.uuid;
  const { updateSkill, skill } = useSkillData(groupUuid, kitUuid, skillUuid);

  if (!skill || !groupUuid) {
    return null;
  }

  return (
    <>
      <AppTopRightMenu.ContentPortal display="append">
        <GrantDialog
          groupUuid={groupUuid}
          objectName={skill.name}
          skillUuid={skill.uuid}
          notice={
            skill.dependencies.length > 0
              ? "This skill can use its dependencies in any group it is shared with, even when those skills, files, and templates are not shared there."
              : undefined
          }
        />
      </AppTopRightMenu.ContentPortal>
      <Page>
        <PageHeader>
          <PageHeader.Title>Skill Settings</PageHeader.Title>
        </PageHeader>
        <PageBody>
          <DisplayEditor
            name={skill.name}
            description={skill.description}
            onNameChange={async (name) => {
              await updateSkill({ name });
              await updateKit({ name });
            }}
            onDescriptionChange={async (description) => {
              await updateSkill({ description });
              await updateKit({ description });
            }}
          />

          <ConnectedExperts
            groupUuid={groupUuid}
            kitUuid={kitUuid}
            skillUuid={skillUuid}
          />

          {kit && <KitDeletionArea kit={kit} />}
        </PageBody>
      </Page>
    </>
  );
}

function ConnectedExperts({
  groupUuid,
  kitUuid,
  skillUuid,
}: {
  groupUuid: string | null;
  kitUuid: string | null;
  skillUuid: string | null;
}) {
  const { expertUuids } = useSkillExpertsData(groupUuid, kitUuid, skillUuid);
  const { experts: ownExperts } = useGroupKitExpertsData(groupUuid);
  const { experts: sharedExperts } = useGroupExpertsData(groupUuid);

  const seenUuids = new Set<string>();
  const allExperts = [...ownExperts, ...sharedExperts].filter((e) => {
    if (seenUuids.has(e.uuid)) return false;
    seenUuids.add(e.uuid);
    return true;
  });

  const connectedExperts = allExperts.filter((e) =>
    expertUuids.includes(e.uuid),
  );

  const ownConnected = connectedExperts.filter(
    (e) => e.group?.uuid === groupUuid,
  );
  const otherConnected = connectedExperts.filter(
    (e) => e.group?.uuid !== groupUuid,
  );

  const otherByGroup = new Map<
    string,
    {
      group: (typeof otherConnected)[number]["group"];
      count: number;
    }
  >();
  otherConnected.forEach((e) => {
    const key = e.group?.uuid ?? "unknown";
    const entry = otherByGroup.get(key);
    if (entry) entry.count++;
    else otherByGroup.set(key, { group: e.group, count: 1 });
  });

  const hiddenCount = expertUuids.length - connectedExperts.length;
  const hasOtherGroups = otherByGroup.size > 0 || hiddenCount > 0;

  return (
    <Column className="gap-4">
      <div>
        <h2 className="text-xl font-bold">
          Connected Experts
          {expertUuids.length > 0 && (
            <span className="text-muted-foreground ml-1 text-base font-normal">
              ({expertUuids.length})
            </span>
          )}
        </h2>
        <p className="text-sm text-gray-600">
          From the Experts Page you can create and link skills to a given
          expert.
        </p>
      </div>

      {expertUuids.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No experts are using this skill yet.
        </p>
      ) : (
        <div className="space-y-4">
          {ownConnected.length > 0 && (
            <div className="space-y-2">
              <h3>This Group</h3>
              <div className="space-y-3">
                {ownConnected.map((expert) => (
                  <div key={expert.uuid} className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={expert.profilePicture ?? undefined} />
                      <AvatarFallback className="text-xs">
                        {getInitials(expert.name, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm">{expert.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasOtherGroups && (
            <div className="space-y-2">
              <h3>Other Groups</h3>
              <div className="space-y-3">
                {[...otherByGroup.values()].map(({ group, count }) => (
                  <p key={group?.uuid ?? "unknown"} className="flex gap-2">
                    <GroupOrUnknownDisplayName group={group} />({count} expert)
                  </p>
                ))}
                {hiddenCount > 0 && (
                  <p className="flex gap-2">
                    <GroupOrUnknownDisplayName group={null} />({hiddenCount}{" "}
                    expert)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Column>
  );
}
